import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { SEO_DEFAULTS, type SEOConfig } from './constants';

export type { SEOConfig } from './constants';

export function useSEO(config: SEOConfig) {
  const location = useLocation();
  const siteUrl = SEO_DEFAULTS.siteUrl || (typeof window !== 'undefined' ? window.location.origin : '');
  const currentUrl = `${siteUrl}${location.pathname}${location.search}`;
  
  const title = config.title 
    ? `${config.title} | ${SEO_DEFAULTS.siteName}`
    : SEO_DEFAULTS.siteName;
  
  const description = config.description || SEO_DEFAULTS.siteDescription;
  const image = config.image || `${siteUrl}${SEO_DEFAULTS.defaultOgImage}`;
  const canonical = config.canonical || currentUrl;
  const ogUrl = config.url || currentUrl;

  useEffect(() => {
    // Update document title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, attribute: 'name' | 'property' = 'name') => {
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Primary meta tags
    updateMetaTag('title', title);
    updateMetaTag('description', description);
    if (config.keywords) {
      updateMetaTag('keywords', config.keywords);
    }

    // Open Graph tags
    updateMetaTag('og:title', title, 'property');
    updateMetaTag('og:description', description, 'property');
    updateMetaTag('og:image', image, 'property');
    updateMetaTag('og:url', ogUrl, 'property');
    updateMetaTag('og:type', config.type || 'website', 'property');
    updateMetaTag('og:site_name', SEO_DEFAULTS.siteName, 'property');

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonical);

    // Robots meta
    if (config.noindex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      // Remove noindex if it exists
      const robotsTag = document.querySelector('meta[name="robots"]');
      if (robotsTag) {
        robotsTag.remove();
      }
    }

    // Structured data (JSON-LD)
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    if (config.structuredData) {
      const dataArray = Array.isArray(config.structuredData) 
        ? config.structuredData 
        : [config.structuredData];
      
      dataArray.forEach((data) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
      });
    }
  }, [title, description, image, canonical, ogUrl, config.keywords, config.type, config.noindex, config.structuredData]);
}

